// Pair seats: loveseats and beds are one piece of furniture for two guests, two places with a ticket each,
// stored as a `_left` / `_right` half (the audience's left and right). Every place that treats pairs --
// the 2D and 3D seat maps, the Room Studio -- asks these helpers instead of comparing loveseat types, so a
// bed is paired, drawn and validated exactly like a loveseat.
// Pure: type-only imports, loadable by `node --test` without an install.
import type { SeatType } from './SeatDto.types';

export type PairKind = 'loveseat' | 'bed';
export type PairSide = 'left' | 'right';

const LOVESEAT_LEFT = 'loveseat_left' as SeatType;
const LOVESEAT_RIGHT = 'loveseat_right' as SeatType;
const BED_LEFT = 'bed_left' as SeatType;
const BED_RIGHT = 'bed_right' as SeatType;

export const PAIR_KINDS: readonly PairKind[] = ['loveseat', 'bed'];

const HALVES: Record<PairKind, Record<PairSide, SeatType>> = {
    loveseat: { left: LOVESEAT_LEFT, right: LOVESEAT_RIGHT },
    bed: { left: BED_LEFT, right: BED_RIGHT }
};

const BY_TYPE = new Map<string, { kind: PairKind; side: PairSide }>(
    PAIR_KINDS.flatMap((kind) =>
        (['left', 'right'] as const).map((side) => [HALVES[kind][side] as string, { kind, side }] as const)
    )
);

/** `loveseat` or `bed` for a pair half, `null` for every other type. */
export function pairKindOf(type: SeatType | string | null | undefined): PairKind | null {
    return type ? (BY_TYPE.get(type)?.kind ?? null) : null;
}

/** Which half a pair seat is, `null` for every other type. */
export function pairSideOf(type: SeatType | string | null | undefined): PairSide | null {
    return type ? (BY_TYPE.get(type)?.side ?? null) : null;
}

export const isPairSeatType = (type: SeatType | string | null | undefined): boolean => pairKindOf(type) !== null;
export const isPairLeftType = (type: SeatType | string | null | undefined): boolean => pairSideOf(type) === 'left';
export const isPairRightType = (type: SeatType | string | null | undefined): boolean => pairSideOf(type) === 'right';

/** The type of one half of a pair kind. */
export function pairSeatType(kind: PairKind, side: PairSide): SeatType {
    return HALVES[kind][side];
}

/** Whether `left` and `right` (in this order, left to right) are the two halves of one piece. */
export function arePairHalves(
    left: SeatType | string | null | undefined,
    right: SeatType | string | null | undefined
): boolean {
    const kind = pairKindOf(left);
    return kind !== null && pairSideOf(left) === 'left' && right === HALVES[kind].right;
}

/** The other half's type: what a half becomes when a left/right mirror swaps the pair. */
export function mirroredPairType<T extends SeatType | string>(type: T): T | SeatType {
    const kind = pairKindOf(type);
    if (!kind) return type;
    return HALVES[kind][pairSideOf(type) === 'left' ? 'right' : 'left'];
}

/**
 * The pairs of one row: every left half matched to the nearest right half of the same kind to its right,
 * with no seat between them (`nonPairsBlock`: any seat; otherwise only other pair halves block). A half
 * without such a partner stays unpaired. `x` is the position along the row.
 */
export function findRowPairs<S extends { type: SeatType | string; x: number }>(
    row: readonly S[],
    options: { nonPairsBlock: boolean }
): { left: S; right: S; kind: PairKind }[] {
    const sorted = [...row].sort((a, b) => a.x - b.x);
    const blockers = options.nonPairsBlock ? sorted : sorted.filter((seat) => isPairSeatType(seat.type));
    const used = new Set<S>();
    const pairs: { left: S; right: S; kind: PairKind }[] = [];
    for (const left of sorted) {
        const kind = pairKindOf(left.type);
        if (!kind || pairSideOf(left.type) !== 'left' || used.has(left)) continue;
        const right = sorted.find((s) => s.type === HALVES[kind].right && !used.has(s) && s.x > left.x);
        if (!right) continue;
        if (blockers.some((s) => s.x > left.x && s.x < right.x)) continue;
        used.add(left);
        used.add(right);
        pairs.push({ left, right, kind });
    }
    return pairs;
}
